import React, { useEffect, useMemo, useState } from "react";
import CustomSelect from "../form/CustomSelect";
import "react-datepicker/dist/react-datepicker.css";
import {
    getStatuses,
    getTypes,
    plansResource,
    fabricantsResource,
    modelsTruckTrailerResource,
} from "../../helpers/configRoutes/backend_helper";
import CustomCalendarRange from "./CustomCalendarRange";

const trailerTruckExpirationOptions = [
    { id: "expired_any", name: "Expired docs" },
    { id: "expiring_any", name: "Docs expiring ≤ 30d" },
    { id: "expired_assurance", name: "Insurance expired" },
    { id: "expiring_assurance", name: "Insurance expiring ≤ 30d" },
    { id: "expired_plate_usd", name: "USD plate expired" },
    { id: "expiring_plate_usd", name: "USD plate expiring ≤ 30d" },
    { id: "expired_plate_mx", name: "MX plate expired" },
    { id: "expiring_plate_mx", name: "MX plate expiring ≤ 30d" },
];

const FiltersPagination = ({
    meta,
    children,
    filters,
    setFilters,
    showFilters,
    statusModel,
    equipmentTruck,
    dateRange = true,
    typeTrailer=true,
    lockedStatusId = null,
    excludedStatusIds = [],
}) => {
    const [statuses, setStatuses] = useState([]);
    const [plans, setPlans] = useState([]);
    const [types, setTypes] = useState([]);
    const [fabricants, setFabricants] = useState([]);
    const [models, setModels] = useState([]);
    const usesSubscriptionFilter = statusModel === "Payment";
    const usesEquipmentExpirationFilter = statusModel === "TrailerTruck";
    const hasEquipmentSubtype =
        statusModel === "TrailerTruck" && typeof equipmentTruck === "boolean";
    const hasLockedStatus = lockedStatusId !== null && lockedStatusId !== undefined && lockedStatusId !== "";
    const statusOptions = useMemo(() => {
        if (usesEquipmentExpirationFilter) {
            return trailerTruckExpirationOptions;
        }

        return statuses.filter(
            (status) => !excludedStatusIds.map(Number).includes(Number(status.id))
        );
    }, [statuses, usesEquipmentExpirationFilter, excludedStatusIds]);

    useEffect(() => {
        const getFilters = async () => {
            if (statusModel === "TrailerTruck") {
                const trailerTruckParams = {
                    select: true,
                    ...(hasEquipmentSubtype ? { truck: equipmentTruck } : {}),
                };
                const typesResponse = await getTypes({
                    params: trailerTruckParams,
                });
                if (typesResponse?.types) setTypes(typesResponse.types);

                const fabricantsResponse = await fabricantsResource("get", {
                    params: trailerTruckParams,
                });
                if (fabricantsResponse?.fabricants) {
                    setFabricants(fabricantsResponse.fabricants);
                }
            } else if (usesSubscriptionFilter) {
                const plansResponse = await plansResource("get", {
                    params: { select: true },
                });
                if (plansResponse.status && plansResponse.plans) {
                    setPlans(plansResponse.plans);
                }
            }

            if (!usesEquipmentExpirationFilter && !hasLockedStatus) {
                const statusesResponse = await getStatuses({
                    params: {
                        model: statusModel ?? "Subscription",
                        ...(hasEquipmentSubtype ? { truck: equipmentTruck } : {}),
                    },
                });
                if (statusesResponse.status && statusesResponse.statuses) {
                    setStatuses(statusesResponse.statuses);
                }
            }
        };

        if (showFilters) {
            getFilters();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        showFilters,
        statusModel,
        usesSubscriptionFilter,
        hasEquipmentSubtype,
        equipmentTruck,
        usesEquipmentExpirationFilter,
        hasLockedStatus,
    ]);

    useEffect(() => {
        if (!hasEquipmentSubtype) {
            return;
        }

        const getModels = async () => {
            const modelsResponse = await modelsTruckTrailerResource("get", {
                params: {
                    select: true,
                    fabricant_id: filters.fabricant_id,
                    truck: equipmentTruck,
                },
            });

            if (modelsResponse?.models) {
                setModels(modelsResponse.models);
            } else {
                setModels([]);
            }
        };

        if (filters.fabricant_id) {
            getModels();
        } else {
            setModels([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.fabricant_id, hasEquipmentSubtype, equipmentTruck]);

    const resetFilters = () => {
        setFilters({
            ...filters,
            search: "",
            plan_id: "",
            status_id: hasLockedStatus ? lockedStatusId : "",
            expiration_filter: "",
            startDate: "",
            endDate: "",
            start_date: "",
            end_date: "",
            model_id: "",
            type_id: "",
            fabricant_id: "",
            page: 1,
        });
    };

    return (
        <>
            {showFilters && (
                <div className="flex w-full mb-4 gap-4 items-start">
                    {/* Filtros */}
                    <div
                        className="
              flex items-center gap-3
              bg-cs-bg
              border border-cs-border
              rounded-2xl
              px-3 py-3
              w-2/3
              shadow-sm
            "
                    >
                        {/* Icono decorativo */}
                        {dateRange &&
                            <CustomCalendarRange
                                isClearable
                                holder="Date Range"
                                valueStart={filters.startDate || filters.start_date}
                                valueEnd={filters.endDate || filters.end_date}
                                filters={filters}
                                setFilters={setFilters}
                            />
                        }

                        {/* Plan / Type */}
                        {(statusModel === "TrailerTruck" || usesSubscriptionFilter) && (
                            <div className="flex items-center gap-2 pr-3 border-r border-cs-border/60">
                                {statusModel === "TrailerTruck" ? (
                                    <>
                                        {typeTrailer &&
                                            <CustomSelect
                                                className="border-none text-[11px] min-w-[120px]"
                                                options={types}
                                                holder="Type"
                                                value={filters.type_id}
                                                onChange={(e) =>
                                                    setFilters({
                                                        ...filters,
                                                        type_id: e.target.value,
                                                        page: 1,
                                                    })
                                                }
                                            />
                                        }
                                        <CustomSelect
                                            className="border-none text-[11px] min-w-[140px]"
                                            options={fabricants}
                                            holder="Make"
                                            value={filters.fabricant_id}
                                            onChange={(e) =>
                                                setFilters({
                                                    ...filters,
                                                    fabricant_id: e.target.value,
                                                    model_id: "",
                                                    page: 1,
                                                })
                                            }
                                        />
                                        <CustomSelect
                                            className="border-none text-[11px] min-w-[140px]"
                                            options={models}
                                            holder="Model"
                                            value={filters.model_id}
                                            onChange={(e) =>
                                                setFilters({
                                                    ...filters,
                                                    model_id: e.target.value,
                                                    page: 1,
                                                })
                                            }
                                            disabled={!filters.fabricant_id}
                                        />
                                    </>
                                ) : (
                                    <CustomSelect
                                        className="border-none text-[11px]"
                                        options={plans}
                                        holder="Subscription"
                                        value={filters.plan_id}
                                        onChange={(e) =>
                                            setFilters({
                                                ...filters,
                                                plan_id: e.target.value,
                                                page: 1,
                                            })
                                        }
                                    />
                                )}
                            </div>
                        )}

                        {/* Status / Alerts */}
                        {!hasLockedStatus && (
                            <div className="flex items-center gap-2 pr-3 border-r border-cs-border/60">
                                <CustomSelect
                                    className="border-none text-[11px] min-w-[170px]"
                                    options={statusOptions}
                                    holder={usesEquipmentExpirationFilter ? "Alerts" : "Status"}
                                    value={usesEquipmentExpirationFilter ? filters.expiration_filter : filters.status_id}
                                    onChange={(e) =>
                                        setFilters({
                                            ...filters,
                                            ...(usesEquipmentExpirationFilter
                                                ? { expiration_filter: e.target.value }
                                                : { status_id: e.target.value }),
                                            page: 1,
                                        })
                                    }
                                />
                            </div>
                        )}

                        {/* Reset */}
                        <button
                            type="button"
                            onClick={resetFilters}
                            className="
                flex items-center gap-1
                text-[10px]
                text-cs-danger
                hover:text-cs-danger
                hover:underline
                transition-colors
                ml-auto
              "
                        >
                            <span className="material-symbols-outlined text-[16px]">
                                restart_alt
                            </span>
                            <span>Reset filters</span>
                        </button>
                    </div>

                    {/* Search */}
                    {statusModel !== "TrailerTruck" && (
                        <div className="w-1/3">
                            <div
                                className="
                  relative
                  bg-cs-bg
                  border border-cs-border
                  rounded-2xl
                  px-3 py-2
                  flex items-center gap-2
                  shadow-sm
                "
                            >
                                <span className="material-symbols-outlined text-[18px] text-cs-muted">
                                    search
                                </span>
                                <input
                                    type="text"
                                    placeholder="Search"
                                    value={filters.search}
                                    onChange={(e) =>
                                        setFilters({
                                            ...filters,
                                            search: e.target.value,
                                            page: 1,
                                        })
                                    }
                                    className="
                    w-full
                    bg-transparent
                    text-xs text-cs-dark
                    placeholder-cs-muted
                    focus:outline-none
                  "
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}

            {children}

            {/* Footer paginación */}
            <div className="flex items-center justify-between py-2 text-[11px]">
                {/* Texto resultados */}
                <div className="text-cs-muted">
                    Showing{" "}
                    <span className="font-semibold text-cs-dark">
                        {meta.from || 0}-{meta.to || 0}
                    </span>{" "}
                    of{" "}
                    <span className="font-semibold text-cs-dark">
                        {meta.total || 0}
                    </span>
                </div>

                {/* Controles */}
                <div className="flex items-center gap-1">
                    {/* Prev */}
                    <button
                        type="button"
                        disabled={filters.page === 1}
                        onClick={() =>
                            setFilters({
                                ...filters,
                                page: filters.page - 1,
                            })
                        }
                        className={`
              flex items-center justify-center w-8 h-8
              rounded-xl border border-cs-border
              text-cs-muted
              transition-colors
              ${filters.page === 1
                                ? "opacity-40 cursor-not-allowed"
                                : "hover:bg-cs-bg-alt hover:text-cs-dark"
                            }
            `}
                    >
                        <span className="material-symbols-outlined text-[18px]">
                            chevron_left
                        </span>
                    </button>

                    {/* Next */}
                    <button
                        type="button"
                        disabled={meta.last_page === filters.page}
                        onClick={() =>
                            setFilters({
                                ...filters,
                                page: filters.page + 1,
                            })
                        }
                        className={`
              flex items-center justify-center w-8 h-8
              rounded-xl border border-cs-border
              text-cs-muted
              transition-colors
              ${meta.last_page === filters.page
                                ? "opacity-40 cursor-not-allowed"
                                : "hover:bg-cs-bg-alt hover:text-cs-dark"
                            }
            `}
                    >
                        <span className="material-symbols-outlined text-[18px]">
                            chevron_right
                        </span>
                    </button>
                </div>
            </div>
        </>
    );
};

export default FiltersPagination;
